import { Injectable } from '@angular/core';
import {
	ComponentController,
	compareEntity,
	filterEmpty,
	filterNull,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectItem } from 'primeng/api';
import { Observable, combineLatest } from 'rxjs';
import {
	distinctUntilChanged,
	first,
	map,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs/operators';

import {
	CoacheesEditObserversState,
	CoacheesEditObserversStore,
} from 'presentation/app-sidebar/sidebar-coachees/coachees-edit-observers/coachees-edit-observers.store';
import { UserApplicationPort } from 'user/port/user-application.port';
import { IUser } from 'user/user';

@Injectable()
@UntilDestroy()
export class CoacheesEditObserversController extends ComponentController<CoacheesEditObserversState> {
	public static BASE_I18N_KEY =
		'presentation.app.sidebar.coachees.editObservers';

	public coachesOptions$: Observable<SelectItem[]>;
	public observersId$: Observable<string[]>;

	private _coachee$: Observable<IUser>;

	constructor(
		store: CoacheesEditObserversStore,
		private _userApplicationPort: UserApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public updateCoacheeId(coacheeId: string): void {
		this.store.update({ coacheeId });
	}

	public updateObservers(): Observable<void> {
		return combineLatest([
			this.select('coacheeId').pipe(filterEmpty()),
			this.select('observersId'),
		]).pipe(
			first(),
			switchMap(([coacheeId, observersId]) =>
				this._userApplicationPort.updateCoacheeObservers({
					observersId,
					coacheeId,
				})
			)
		);
	}

	public updateObserversId(observersId: string[]): void {
		this.store.update({ observersId });
	}

	private _initObservables() {
		this.observersId$ = this.select('observersId').pipe(
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this._coachee$ = this.select('coacheeId').pipe(
			filterEmpty(),
			switchMap((coacheeId) =>
				this._userApplicationPort.observeUserById(coacheeId)
			),
			map((data) => data.data),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.coachesOptions$ = this._coachee$.pipe(
			filterNull(),
			distinctUntilChanged(compareEntity),
			switchMap((coachee) =>
				this._userApplicationPort
					.observeSearchedCoachesIncludingPartners({
						organizationId: coachee.organizationId,
						chunkSize: 100,
						offset: 0,
					})
					.pipe(
						map((data) => data.data),
						map((coaches) =>
							coaches
								.filter((coach) => coach.id !== coachee?.coachId)
								.map(
									(coach) =>
										({
											label: coach.name,
											value: coach.id,
										} as SelectItem)
								)
						)
					)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	private _initListeners() {
		this._coachee$
			.pipe(
				filterNull(),
				tap((coachee) => this.store.update({ observersId: coachee.observersId })),
				untilDestroyed(this)
			)
			.subscribe();
	}
}
